import { useEffect, useRef, RefObject } from 'react';
import { useDragLayer } from 'react-dnd';

const AUTO_SCROLL_EDGE_PX = 80;
const AUTO_SCROLL_MAX_SPEED = 16;

export const useAutoScrollOnDrag = (containerRef: RefObject<HTMLDivElement | null>) => {
  const { isDragging, clientOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    clientOffset: monitor.getClientOffset(),
  }));

  const rafRef = useRef<number | null>(null);
  const speedRef = useRef(0);

  useEffect(() => {
    if (!isDragging || !clientOffset || !containerRef.current) {
      speedRef.current = 0;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const y = clientOffset.y;

    let speed = 0;
    if (y > rect.bottom - AUTO_SCROLL_EDGE_PX) {
      const ratio = Math.min((y - (rect.bottom - AUTO_SCROLL_EDGE_PX)) / AUTO_SCROLL_EDGE_PX, 1);
      speed = AUTO_SCROLL_MAX_SPEED * ratio;
    } else if (y < rect.top + AUTO_SCROLL_EDGE_PX) {
      const ratio = Math.min((rect.top + AUTO_SCROLL_EDGE_PX - y) / AUTO_SCROLL_EDGE_PX, 1);
      speed = -AUTO_SCROLL_MAX_SPEED * ratio;
    }

    speedRef.current = speed;

    if (speed !== 0 && !rafRef.current) {
      const tick = () => {
        if (speedRef.current === 0) {
          rafRef.current = null;
          return;
        }
        container.scrollTop += speedRef.current;
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [isDragging, clientOffset, containerRef]);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);
};
